import { invalidateVillageReadCache, request } from './api'

export type VillageRevisionAck = number | null
export type VillageLiveListener = (
  requiredRevision: number | null,
) => VillageRevisionAck | Promise<VillageRevisionAck>

interface VillageSyncProbe {
  villageId: string
  villageRevision: number
}

interface Subscriber {
  id: number
  listener: VillageLiveListener
  appliedRevision: number | null
  pendingRevision: number | null
  forcePending: boolean
  drainRequest: Promise<void> | null
  retryTimer: number | null
  retryAttempt: number
  disposed: boolean
}

interface Target {
  worldId: string
  villageId: string
  observedRevision: number | null
  subscribers: Map<number, Subscriber>
  syncRequest: Promise<boolean> | null
}

const targets = new Map<string, Target>()
const SAFETY_RECONCILE_MS = 60_000
const RESYNC_RETRY_MS = [250, 500, 1_000, 2_000, 5_000] as const
let heartbeat: number | null = null
let subscriberSequence = 0

function key(worldId: string, villageId: string): string {
  return `${worldId}:${villageId}`
}

function target(worldId: string, villageId: string): Target {
  const id = key(worldId, villageId)
  const existing = targets.get(id)
  if (existing) return existing
  const created: Target = {
    worldId,
    villageId,
    observedRevision: null,
    subscribers: new Map(),
    syncRequest: null,
  }
  targets.set(id, created)
  return created
}

function validRevision(value: unknown): value is number {
  return Number.isSafeInteger(value) && Number(value) >= 0
}

function documentIsHidden(): boolean {
  return typeof document !== 'undefined' && document.hidden
}

/**
 * A view composed from multiple village read models can only acknowledge the
 * oldest revision it actually applied. Returning the minimum keeps the live bus
 * conservative when parallel canonical reads straddle a commit.
 */
export function minimumVillageRevision(
  ...values: readonly unknown[]
): VillageRevisionAck {
  const revisions = values.filter(validRevision)
  if (revisions.length === 0) return null
  return Math.min(...revisions)
}

function currentPendingRevision(subscriber: Subscriber): number | null {
  return subscriber.pendingRevision
}

// Subscriber lifecycle fields are intentionally mutable from unsubscribe and
// timer callbacks while an async listener is in flight. Read them through
// accessors so each decision observes the current value after an await.
function subscriberIsDisposed(subscriber: Subscriber): boolean {
  return subscriber.disposed
}

function subscriberRetryIsIdle(subscriber: Subscriber): boolean {
  return subscriber.retryTimer === null
}

function subscriberNeedsRevision(
  subscriber: Subscriber,
  requiredRevision: number,
): boolean {
  return (
    subscriber.appliedRevision === null ||
    subscriber.appliedRevision < requiredRevision
  )
}

function clearSubscriberRetry(subscriber: Subscriber): void {
  if (subscriber.retryTimer !== null && typeof window !== 'undefined')
    window.clearTimeout(subscriber.retryTimer)
  subscriber.retryTimer = null
  subscriber.retryAttempt = 0
}

function mergePendingRevision(subscriber: Subscriber, revision: number): void {
  if (!subscriberNeedsRevision(subscriber, revision)) return
  subscriber.pendingRevision = Math.max(
    subscriber.pendingRevision ?? 0,
    revision,
  )
}

function scheduleSubscriberRetry(
  item: Target,
  subscriber: Subscriber,
  revision: number,
): void {
  if (
    typeof window === 'undefined' ||
    subscriber.disposed ||
    subscriber.retryTimer !== null
  )
    return
  mergePendingRevision(subscriber, revision)
  if (documentIsHidden()) return
  const delay =
    RESYNC_RETRY_MS[
      Math.min(subscriber.retryAttempt, RESYNC_RETRY_MS.length - 1)
    ]
  subscriber.retryAttempt += 1
  subscriber.retryTimer = window.setTimeout(() => {
    subscriber.retryTimer = null
    if (subscriber.disposed || documentIsHidden()) return
    void drainSubscriber(item, subscriber)
  }, delay)
}

async function drainSubscriber(
  item: Target,
  subscriber: Subscriber,
): Promise<void> {
  if (subscriberIsDisposed(subscriber) || documentIsHidden()) return
  if (subscriber.drainRequest !== null) return subscriber.drainRequest

  const drain = (async () => {
    while (
      !subscriberIsDisposed(subscriber) &&
      !documentIsHidden() &&
      (subscriber.forcePending || subscriber.pendingRevision !== null)
    ) {
      const requiredRevision = subscriber.pendingRevision
      subscriber.pendingRevision = null
      const force = subscriber.forcePending
      subscriber.forcePending = false
      if (
        !force &&
        requiredRevision !== null &&
        !subscriberNeedsRevision(subscriber, requiredRevision)
      )
        continue
      let applied: VillageRevisionAck
      try {
        applied = await subscriber.listener(requiredRevision)
      } catch {
        applied = null
      }
      if (subscriberIsDisposed(subscriber)) return

      if (validRevision(applied)) {
        if (
          subscriber.appliedRevision === null ||
          applied > subscriber.appliedRevision
        )
          subscriber.appliedRevision = applied
      }

      if (
        requiredRevision !== null &&
        (!validRevision(applied) || applied < requiredRevision)
      ) {
        scheduleSubscriberRetry(item, subscriber, requiredRevision)
        return
      }

      clearSubscriberRetry(subscriber)
      const pendingRevision = currentPendingRevision(subscriber)
      if (
        pendingRevision !== null &&
        !subscriberNeedsRevision(subscriber, pendingRevision)
      )
        subscriber.pendingRevision = null
    }
  })()
  subscriber.drainRequest = drain
  try {
    await drain
  } finally {
    subscriber.drainRequest = null
    if (
      !subscriberIsDisposed(subscriber) &&
      !documentIsHidden() &&
      subscriberRetryIsIdle(subscriber) &&
      (subscriber.forcePending || subscriber.pendingRevision !== null)
    )
      void drainSubscriber(item, subscriber)
  }
}

function enqueueSubscriber(
  item: Target,
  subscriber: Subscriber,
  requiredRevision: number | null,
  force: boolean,
): Promise<void> {
  if (subscriber.disposed) return Promise.resolve()
  if (force) subscriber.forcePending = true
  if (requiredRevision !== null)
    mergePendingRevision(subscriber, requiredRevision)
  if (documentIsHidden()) return Promise.resolve()
  return drainSubscriber(item, subscriber)
}

function subscribersNeedingRevision(
  item: Target,
  revision: number,
): Subscriber[] {
  return [...item.subscribers.values()].filter(
    (subscriber) =>
      !subscriber.disposed && subscriberNeedsRevision(subscriber, revision),
  )
}

/**
 * Accepts a revision that the server has already committed and published via
 * the transactional realtime outbox. This is the primary queue/live update
 * path: browser clocks never infer that a server-side transition happened.
 *
 * Subscribers are scheduled independently. A slow AppShell/resource request
 * can therefore never hold a queue view behind it; each read model advances to
 * the confirmed revision on its own single-flight lane.
 */
export function acceptVillageRealtimeRevision(
  worldId: string,
  villageId: string,
  revision: number,
): void {
  if (!validRevision(revision)) return
  const item = targets.get(key(worldId, villageId))
  if (!item) return
  if (item.observedRevision === null || revision > item.observedRevision)
    item.observedRevision = revision

  const selected = subscribersNeedingRevision(item, revision)
  if (selected.length === 0) return
  invalidateVillageReadCache(item.worldId, item.villageId)
  for (const subscriber of selected)
    void enqueueSubscriber(item, subscriber, revision, false)
}

export async function reconcileVillage(
  worldId: string,
  villageId: string,
): Promise<boolean> {
  const item = target(worldId, villageId)
  if (item.syncRequest) return item.syncRequest
  item.syncRequest = (async () => {
    try {
      const probe = await request<VillageSyncProbe>(
        `/api/v2/worlds/${encodeURIComponent(worldId)}/villages/${encodeURIComponent(villageId)}/sync`,
      )
      if (
        item.observedRevision === null ||
        probe.villageRevision > item.observedRevision
      )
        item.observedRevision = probe.villageRevision

      const selected = subscribersNeedingRevision(item, probe.villageRevision)
      if (selected.length === 0) return false
      invalidateVillageReadCache(worldId, villageId)
      await Promise.all(
        selected.map((subscriber) =>
          enqueueSubscriber(item, subscriber, probe.villageRevision, false),
        ),
      )
      return true
    } finally {
      item.syncRequest = null
    }
  })()
  return item.syncRequest
}

export function observeVillageRevision(
  worldId: string,
  villageId: string,
  revision: number,
): void {
  if (!validRevision(revision)) return
  const item = target(worldId, villageId)
  if (item.observedRevision === null || revision > item.observedRevision)
    item.observedRevision = revision
}

// Local mutations still invalidate immediately. Server realtime publication then
// confirms the committed revision, making local mutation broadcasts an optional
// latency optimization rather than a correctness mechanism.
export function invalidateVillage(worldId: string, villageId: string): void {
  const item = target(worldId, villageId)
  item.observedRevision = null
  invalidateVillageReadCache(worldId, villageId)
  for (const subscriber of item.subscribers.values()) {
    subscriber.appliedRevision = null
    void enqueueSubscriber(item, subscriber, null, true)
  }
}

export function subscribeVillage(
  worldId: string,
  villageId: string,
  listener: VillageLiveListener,
): () => void {
  const item = target(worldId, villageId)
  subscriberSequence += 1
  const subscriber: Subscriber = {
    id: subscriberSequence,
    listener,
    appliedRevision: null,
    pendingRevision: null,
    forcePending: false,
    drainRequest: null,
    retryTimer: null,
    retryAttempt: 0,
    disposed: false,
  }
  item.subscribers.set(subscriber.id, subscriber)
  ensureHeartbeat()
  return () => {
    subscriber.disposed = true
    clearSubscriberRetry(subscriber)
    item.subscribers.delete(subscriber.id)
    if (item.subscribers.size === 0) targets.delete(key(worldId, villageId))
    ensureHeartbeat()
  }
}

function activeTargets(): Target[] {
  return [...targets.values()].filter((item) => item.subscribers.size > 0)
}

export function reconcileActiveVillages(): void {
  if (documentIsHidden()) return
  for (const item of activeTargets())
    void reconcileVillage(item.worldId, item.villageId)
}

function resumePendingSubscribers(): void {
  if (documentIsHidden()) return
  for (const item of activeTargets()) {
    for (const subscriber of item.subscribers.values()) {
      if (
        subscriber.forcePending ||
        subscriber.pendingRevision !== null ||
        (item.observedRevision !== null &&
          subscriberNeedsRevision(subscriber, item.observedRevision))
      ) {
        if (item.observedRevision !== null)
          mergePendingRevision(subscriber, item.observedRevision)
        void drainSubscriber(item, subscriber)
      }
    }
  }
}

function ensureHeartbeat(): void {
  if (typeof window === 'undefined') return
  const needed = activeTargets().length > 0
  if (needed && heartbeat === null)
    heartbeat = window.setInterval(reconcileActiveVillages, SAFETY_RECONCILE_MS)
  if (!needed && heartbeat !== null) {
    window.clearInterval(heartbeat)
    heartbeat = null
  }
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      resumePendingSubscribers()
      reconcileActiveVillages()
    }
  })
  window.addEventListener('focus', () => {
    resumePendingSubscribers()
    reconcileActiveVillages()
  })
  window.addEventListener('online', () => {
    resumePendingSubscribers()
    reconcileActiveVillages()
  })
  window.addEventListener('ci:realtime-open', reconcileActiveVillages)
  window.addEventListener('ci:mutation-committed', () => {
    for (const item of activeTargets())
      invalidateVillage(item.worldId, item.villageId)
  })
  if ('BroadcastChannel' in window) {
    const channel = new BroadcastChannel('craft-imperium-live-v62')
    channel.addEventListener('message', reconcileActiveVillages)
  }
}
